/* eslint-disable @typescript-eslint/ban-ts-comment */
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { RequiredStringSchema } from 'yup/lib/string';
import { AnyObject } from 'yup/lib/types';
import { dateConversion, flattenFormBuilderToArray } from '.';
import {
	CurrencyFieldConfiguration,
	DateFieldConfiguration,
	EmailFieldConfiguration,
	FieldConfiguration,
	LookupFieldConfiguration,
	MediaFieldConfiguration,
	NumberFieldConfiguration,
	SelectFieldConfiguration,
	StringFieldConfiguration,
} from '../../../api/ConfigApi';
import {
	DATE_FIELD_VALIDATIONS,
	DATE_PRECISION_INPUT_FORMAT,
	FIELD_TYPES,
	FORM_MESSAGE,
	NUMBER_FIELD_VALIDATIONS,
	STRING_FIELD_VALIDATIONS,
} from '../data/constants';
import { FormBuilder, FormShape } from '../types';

type ValidationTypes = 'string' | 'date' | 'array' | 'number';
type ValidationParameters = yup.StringSchema<string, AnyObject, string>;
interface Validations {
	type: keyof ValidationParameters;
	params: (
		| RegExp
		| string
		| number
		| string[]
		| ((number: any) => boolean)
		| ((string: string) => boolean)
		| ((value: any, path: any) => boolean)
		| {
				is: (conditions: any) => boolean | string;
				then: RequiredStringSchema<string, AnyObject>;
		  }
	)[];
}

export const getFieldLevelValidation = (
	fields: FieldConfiguration[],
	overrideRequiredFields?: boolean,
) => {
	const schema = fields.reduce((schema, field) => {
		const { name } = field;
		const isObject = name.indexOf('.') >= 0;
		const validations = [] as Validations[];
		let validationType = 'string' as ValidationTypes;

		if (field.condition && !field.optional) {
			const conditions = field.condition;
			validations.push({
				type: 'when',
				params: [
					Object.keys(conditions),
					{
						is: (...currentValuesInDependentFields) => {
							// Check that every condition is met for the dependent field
							const test = Object.keys(conditions).every((key, index) => {
								const possibleOkVales = conditions[key];
								const currentValue = currentValuesInDependentFields[index];
								const conditionMet = possibleOkVales.includes(
									currentValue?.toString(),
								);
								return conditionMet;
							});
							return test;
						},
						then: yup.string().required(FORM_MESSAGE.required),
					},
				],
			});
		}

		switch (field.type) {
			case FIELD_TYPES.string:
				const stringField = field as StringFieldConfiguration;
				if (
					!overrideRequiredFields &&
					!stringField.optional &&
					!stringField.condition
				) {
					validations.push({
						type: 'required',
						params: [FORM_MESSAGE.required],
					});
				}
				if (stringField.regex) {
					validations.push({
						type: 'test',
						params: [
							'validRegex',
							STRING_FIELD_VALIDATIONS.REGEX_ERROR_MESSAGE + stringField.regex,
							(value: string) => {
								const regex = new RegExp(stringField.regex);
								if (value !== '') {
									return regex.test(value);
								}
								return true;
							},
						],
					});
				}
				break;
			case FIELD_TYPES.select:
				const selectField = field as SelectFieldConfiguration;
				const repeatable = selectField.repeatable;

				if (repeatable) {
					validationType = 'array';
				}

				if (
					!overrideRequiredFields &&
					repeatable &&
					!selectField.optional &&
					selectField.values.length > 1
				) {
					validations.push({
						type: 'required',
						params: [FORM_MESSAGE.required],
					});
				}
				if (
					!overrideRequiredFields &&
					!repeatable &&
					!selectField.optional &&
					!selectField.condition
				) {
					validations.push({
						type: 'required',
						params: [FORM_MESSAGE.required],
					});
				}
				break;
			case FIELD_TYPES.date:
				const dateField = field as DateFieldConfiguration;

				validations.push({
					type: 'nullable',
					params: [],
				});
				if (
					!overrideRequiredFields &&
					!dateField.optional &&
					!dateField.condition
				) {
					validations.push({
						type: 'required',
						params: [FORM_MESSAGE.required],
					});
				}
				// Check if valid before all else
				validations.push({
					type: 'test',
					params: [
						'validDate',
						// TODO: future improvement; add precision format to error message across FEv2 (from QE-3531)
						DATE_FIELD_VALIDATIONS.INVALID_DATE_ERROR_MESSAGE,
						(date: string | null) => {
							if (!date) {
								return true;
							}
							const value = dateConversion.dateFormStringToDayJs(
								date,
								dateField.precision,
							);
							return value?.isValid();
						},
					],
				});

				const { minDate, maxDate } =
					dateConversion.getDateBoundaryForDateField(dateField);

				if (minDate) {
					const minDateError =
						DATE_FIELD_VALIDATIONS.MIN_DATE_ERROR_MESSAGE +
						minDate.format(DATE_PRECISION_INPUT_FORMAT[dateField.precision]);
					validations.push({
						type: 'test',
						params: [
							'min',
							minDateError,
							(date: string | null) => {
								if (!date) {
									return true;
								}
								const currDateValue = dateConversion.dateFormStringToDayJs(
									date,
									dateField.precision,
								);
								return currDateValue >= minDate;
							},
						],
					});
				}

				if (maxDate) {
					const maxDateError =
						DATE_FIELD_VALIDATIONS.MAX_DATE_ERROR_MESSAGE +
						maxDate.format(DATE_PRECISION_INPUT_FORMAT[dateField.precision]);

					validations.push({
						type: 'test',
						params: [
							'max',
							maxDateError,
							(date: string | null) => {
								if (!date) {
									return true;
								}
								const currDateValue = dateConversion.dateFormStringToDayJs(
									date,
									dateField.precision,
								);
								return currDateValue <= maxDate;
							},
						],
					});
				}
				break;
			case FIELD_TYPES.email:
				const emailField = field as EmailFieldConfiguration;
				if (
					!overrideRequiredFields &&
					!emailField.optional &&
					!emailField.condition
				) {
					validations.push({
						type: 'required',
						params: [FORM_MESSAGE.required],
					});
				}
				validations.push({
					type: 'email',
					params: ['Invalid email'],
				});
				break;
			case FIELD_TYPES.number:
				const numberField = field as NumberFieldConfiguration;
				validations.push({
					type: 'matches',
					params: [
						NUMBER_FIELD_VALIDATIONS.MUST_BE_A_DIGIT_REGEX,
						NUMBER_FIELD_VALIDATIONS.MUST_BE_A_DIGIT_MESSAGE,
					],
				});
				if (
					!overrideRequiredFields &&
					!numberField.optional &&
					!numberField.condition
				) {
					validations.push({
						type: 'required',
						params: [FORM_MESSAGE.required],
					});
				}
				if (numberField.minimum) {
					validations.push({
						type: 'test',
						params: [
							'max',
							NUMBER_FIELD_VALIDATIONS.MIN_VALUE_ERROR_MESSAGE +
								numberField.minimum,
							(number: any) => number >= parseInt(numberField.minimum),
						],
					});
				}
				if (numberField.maximum) {
					validations.push({
						type: 'test',
						params: [
							'max',
							NUMBER_FIELD_VALIDATIONS.MAX_VALUE_ERROR_MESSAGE +
								numberField.maximum,
							(number: any) => number <= parseInt(numberField.maximum),
						],
					});
				}
				if (numberField.decimalPlaces) {
					validations.push({
						type: 'test',
						params: [
							'maxDigitsAfterDecimal',
							NUMBER_FIELD_VALIDATIONS.DECIMAL_PLACES_ERROR_MESSAGE +
								numberField.decimalPlaces +
								NUMBER_FIELD_VALIDATIONS.DECIMAL_PLACES_ERROR_MESSAGE_2,
							(number: any) => {
								const numStr = String(number);
								const decimalPoints: number =
									numStr?.length && numStr.includes('.')
										? numStr.split('.')[1].length
										: 0;

								return decimalPoints <= parseInt(numberField.decimalPlaces, 10);
							},
						],
					});
				}
				break;
			case FIELD_TYPES.currency:
				const currencyField = field as CurrencyFieldConfiguration;
				if (!overrideRequiredFields && !currencyField.optional) {
					validations.push({
						type: 'required',
						params: [FORM_MESSAGE.required],
					});
				}
				validations.push({
					type: 'test',
					params: [
						'maxDigitsAfterDecimal',
						NUMBER_FIELD_VALIDATIONS.DECIMAL_PLACES_ERROR_MESSAGE +
							'2' +
							NUMBER_FIELD_VALIDATIONS.DECIMAL_PLACES_ERROR_MESSAGE_2,
						(number: any) => {
							const numStr = String(number);
							const decimalPoints: number =
								numStr?.length && numStr.includes('.')
									? numStr.split('.')[1].length
									: 0;

							return decimalPoints <= 2;
						},
					],
				});
				break;
			case FIELD_TYPES.lookup:
				const lookupField = field as LookupFieldConfiguration;
				if (
					!overrideRequiredFields &&
					!lookupField.optional &&
					!lookupField.condition
				) {
					validations.push({
						type: 'required',
						params: [FORM_MESSAGE.required],
					});
				}
				break;
			case FIELD_TYPES.group:
				validationType = 'array';
				validations.push({
					type: 'required',
					params: [FORM_MESSAGE.required],
				});
				break;
			case FIELD_TYPES.media:
				validationType = 'array';
				const mediaField = field as MediaFieldConfiguration;
				if (!overrideRequiredFields && !mediaField.optional) {
					validations.push({
						type: 'required',
						params: [FORM_MESSAGE.required],
					});
				}
				break;
			default:
				break;
		}

		if (!yup[validationType]) {
			return schema;
		}

		let validator = yup[validationType]();

		validations.forEach((validation) => {
			const { params, type } = validation;
			/* @ts-ignore */
			if (!validator[type]) {
				return;
			}
			if (validationType === 'array' && field.type === FIELD_TYPES.select) {
				/* @ts-ignore */
				validator = yup.array(yup.string()).min(1, FORM_MESSAGE.required);
			}
			if (validationType === 'array' && field.type === FIELD_TYPES.group) {
				/* @ts-ignore */
				validator = yup.array().of(getFieldLevelValidation(field.fields));
			}
			if (validationType === 'array' && field.type === FIELD_TYPES.media) {
				/* @ts-ignore */
				validator = yup
					.array()
					.of(
						yup.object().shape({
							locator: yup.string().required(FORM_MESSAGE.required),
						}),
					)
					.min(1, FORM_MESSAGE.required);
			} else {
				/* @ts-ignore */
				validator = validator[type](...params);
			}
		});
		if (!isObject) {
			return schema.concat(yup.object().shape({ [name]: validator }));
		}

		const reversePath = name.split('.').reverse();

		const currNestedObject = reversePath.slice(1).reduce(
			/* @ts-ignore */
			(yupObj, path, index, source) => {
				/* @ts-ignore */
				if (!isNaN(path)) {
					return { array: yup.array().of(yup.object().shape(yupObj)) };
				}
				if (yupObj.array) {
					return { [path]: yupObj.array };
				}
				return { [path]: yup.object().shape(yupObj) };
			},
			{ [reversePath[0]]: validator },
		);
		/* @ts-ignore */
		const newSchema = yup.object().shape(currNestedObject);
		return schema.concat(newSchema);
	}, yup.object().shape({}));

	return schema;
};

const getValidationSchema = (
	form: FormShape<FormBuilder>,
	overrideRequiredFields = false,
) =>
	yupResolver(
		getFieldLevelValidation(
			flattenFormBuilderToArray(form),
			overrideRequiredFields,
		),
	);

export const getValidationSchemaWithoutResolver = (
	form: FormShape<FormBuilder>,
	overrideRequiredFields = false,
) => {
	return getFieldLevelValidation(
		flattenFormBuilderToArray(form),
		overrideRequiredFields,
	);
};

export default getValidationSchema;
